/**
 * HP Migration Utility
 * 
 * This utility provides functions to standardize HP representation across the application,
 * converting legacy hp/maxHp separate fields into the standard hpObj format { current, max }.
 * 
 * Used during data transitions to ensure consistent HP format without data loss.
 */

class HPMigrationUtility {
  /**
   * Ensures that all entities in an array have the standardized hpObj format
   * 
   * @param {Array} entities - An array of entity objects (monsters, characters, tokens)
   * @returns {Array} - The same array with standardized hpObj on all entities
   */
  static migrateEntities(entities) {
    if (!Array.isArray(entities)) {
      console.warn('HPMigrationUtility: Expected an array of entities');
      return entities;
    }
    
    return entities.map(entity => this.migrateEntity(entity));
  }
  
  /**
   * Ensures that a single entity has the standardized hpObj format
   * 
   * @param {Object} entity - An entity object (monster, character, token)
   * @returns {Object} - The entity with standardized hpObj
   */
  static migrateEntity(entity) {
    if (!entity || typeof entity !== 'object') {
      return entity;
    }
    
    // Make a shallow copy to avoid mutating the original
    const result = { ...entity };
    
    // If entity already has a properly formatted hpObj, use it
    if (result.hpObj && typeof result.hpObj === 'object' && 
        'current' in result.hpObj && 'max' in result.hpObj) {
      // Already has proper hpObj, but ensure legacy fields match for compatibility
      result.hp = result.hpObj.current;
      result.maxHp = result.hpObj.max;
      return result;
    }
    
    // Handle legacy case where hp is an object with { current, max }
    if (result.hp && typeof result.hp === 'object') {
      // @date_of_change 2025-09-10 - Handle both 'max' and 'maximum' field names
      // hp object as { current, max } or { current, maximum }
      if ('current' in result.hp || 'max' in result.hp || 'maximum' in result.hp) {
        const currentVal = result.hp.current;
        const maxVal = result.hp.max !== undefined ? result.hp.max : 
                      (result.hp.maximum !== undefined ? result.hp.maximum : currentVal);
        const unified = {
          current: typeof currentVal === 'number' ? currentVal : parseInt(currentVal) || 0,
          max: typeof maxVal === 'number' ? maxVal : parseInt(maxVal) || 0
        };
        result.hpObj = unified;
        result.hp = unified.current;
        result.maxHp = unified.max;
        return result;
      }
      // EntityMigrator style: hp = { average, formula }
      if ('average' in result.hp) {
        const avg = result.hp.average;
        const unified = {
          current: typeof avg === 'number' ? avg : parseInt(avg) || 0,
          max: typeof avg === 'number' ? avg : parseInt(avg) || 0
        };
        result.hpObj = unified;
        result.hp = unified.current;
        result.maxHp = unified.max;
        return result;
      }
    }
    
    // @date_of_change 2025-09-10 - Handle hitPoints object format correctly
    // Extract current and max HP values from various possible formats
    const current = (typeof result.hp === 'number' ? result.hp : undefined)
      ?? (result.hitPoints && typeof result.hitPoints === 'object' ? result.hitPoints.current : result.hitPoints)
      ?? result.hpObj?.current
      ?? 0;
    const max = (typeof result.maxHp === 'number' ? result.maxHp : undefined)
      ?? (result.hitPoints && typeof result.hitPoints === 'object' ? 
          (result.hitPoints.max ?? result.hitPoints.maximum) : undefined)
      ?? result.maxHitPoints
      ?? result.hpObj?.max
      ?? (typeof current === 'number' ? current : 0);
    
    // Create standardized hpObj
    result.hpObj = {
      current: typeof current === 'number' ? current : parseInt(current) || 0,
      max: typeof max === 'number' ? max : parseInt(max) || 0
    };
    
    // @date_of_change 2025-09-10 - Debug logging for HP migration
    console.log(`[HPMigrationUtility] Migrated entity ${result.name}: hp=${result.hp} (${typeof result.hp}), maxHp=${result.maxHp} (${typeof result.maxHp}), hpObj=`, result.hpObj);
    
    // Ensure legacy fields match for compatibility
    result.hp = result.hpObj.current;
    result.maxHp = result.hpObj.max;
    
    return result;
  }
  
  /**
   * Scans an object recursively for any entity-like objects and applies HP migration
   * Useful for deep objects like saved game states
   * 
   * @param {Object} data - Any data object that might contain entities
   * @returns {Object} - The data with all entities having standardized hpObj
   */
  static migrateDeep(data) {
    if (!data || typeof data !== 'object') {
      return data;
    }
    
    if (Array.isArray(data)) {
      return data.map(item => this.migrateDeep(item));
    }
    
    // Check if this object looks like an entity with HP
    if ((data.hp !== undefined || data.maxHp !== undefined || data.hpObj !== undefined) && 
        (data.type === 'monster' || data.type === 'character' || data.type === 'token' || data.type === 'player')) {
      return this.migrateEntity(data);
    }
    
    // Recursively process all properties
    const result = { ...data };
    Object.keys(result).forEach(key => {
      if (result[key] && typeof result[key] === 'object') {
        result[key] = this.migrateDeep(result[key]);
      }
    });
    
    return result;
  }
}

export default HPMigrationUtility;
